import React from 'react';
import ServicePageTemplate from './ServicePageTemplate';
import { HeartPulse, Repeat, Clock, Users, ShieldCheck, MapPin } from 'lucide-react';

const TherapyRehabTransportation = () => {
  const seo = {
    title: 'Physical Therapy & Rehabilitation Transportation Houston | MoveraCare',
    description: 'Non-emergency transportation to physical therapy, occupational therapy, rehabilitation, IME, and FCE appointments across Greater Houston. Recurring and wait-and-return options available.',
    path: '/services/therapy-rehabilitation-transportation',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Physical Therapy & Rehabilitation Transportation',
      serviceType: 'Non-Emergency Medical Transportation',
      areaServed: 'Greater Houston, TX',
      provider: { '@type': 'MedicalBusiness', name: 'MoveraCare Mobility', telephone: '+1-832-925-2128', url: 'https://www.moveracaremobility.com' },
    },
  };

  return (
    <ServicePageTemplate
      testid="service-therapy-rehab"
      seo={seo}
      eyebrow="THERAPY & REHABILITATION"
      heading="Reliable Transportation for Physical Therapy &amp; Rehabilitation"
      intro="MoveraCare Mobility provides scheduled non-emergency medical transportation for patients attending physical therapy, occupational therapy, rehabilitation, orthopedic follow-up, IME, and FCE appointments across Greater Houston."
      who={{
        title: 'For Patients, Injured Workers, and Coordinators',
        bullets: [
          'Patients attending physical therapy or occupational therapy',
          'Injured workers with authorized Workers’ Compensation therapy',
          'Post-surgery orthopedic follow-up transportation',
          'Rehabilitation program participants',
          'Case managers and adjusters coordinating recurring therapy',
          'Hospitals and clinics referring outpatient therapy trips',
        ],
      }}
      included={{
        title: 'Therapy & Rehabilitation Transportation Includes',
        bullets: [
          'Physical therapy transportation',
          'Occupational therapy transportation',
          'Rehabilitation appointment transportation',
          'Independent Medical Examination (IME) transportation',
          'Functional Capacity Evaluation (FCE) transportation',
          'Orthopedic follow-up appointment transportation',
          'Recurring standing schedules',
          'Wait-and-return coordination when arranged in advance',
        ],
      }}
      howItWorks={{
        title: 'How Therapy Transportation Is Scheduled',
        steps: [
          { title: 'Trip / Schedule Request', desc: 'Share patient mobility, appointment location, therapy schedule, and authorization info if applicable.' },
          { title: 'Confirmation', desc: 'MoveraCare confirms the assigned vehicle, pickup window, and any wait-and-return arrangement.' },
          { title: 'Ongoing Trips', desc: 'Recurring therapy schedules run on a standing plan with operational communication.' },
          { title: 'Trip Documentation', desc: 'Trip records available to support authorization, case notes, and billing workflows.' },
        ],
      }}
      why={{
        title: 'Why Therapy Coordinators Rely on MoveraCare',
        items: [
          { icon: Repeat, title: 'Standing Schedule Reliability', desc: 'Support for weekly and multi-week therapy schedules.' },
          { icon: HeartPulse, title: 'Wheelchair & Ambulatory', desc: 'Options based on patient mobility.' },
          { icon: Clock, title: 'Wait-and-Return', desc: 'Driver-waiting service arranged in advance for shorter appointments.' },
          { icon: Users, title: 'Driver Assistance', desc: 'Drivers assist passengers during transport.' },
          { icon: ShieldCheck, title: 'Documentation-Ready', desc: 'Trip records support authorization workflows.' },
          { icon: MapPin, title: 'Greater Houston Coverage', desc: 'Service across our verified operating area.' },
        ],
      }}
      faqs={[
        { question: 'Can wait-and-return be arranged for a 45-minute PT session?', answer: 'Yes. Wait-and-return can be scheduled when arranged in advance, subject to driver availability and applicable wait-time charges.' },
        { question: 'Do you support Workers’ Compensation therapy transportation?', answer: 'Yes. We support authorized non-emergency transportation for injured workers to Workers’ Compensation-approved therapy destinations.' },
        { question: 'Can therapy schedules be set up as recurring?', answer: 'Yes. We support recurring transportation for multi-week therapy programs.' },
        { question: 'Do you transport patients to IME or FCE appointments?', answer: 'Yes, where the passenger is medically stable and appropriate for non-emergency transportation.' },
      ]}
    />
  );
};

export default TherapyRehabTransportation;
